import React, { useCallback } from 'react';

import { Stack, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { IssueCreateEmbedSuccessPayload } from '@atlassian/gic-anywhere';

import { SidePanel } from '@confluence/side-panel';

import { AiSubFooter } from './AiSubFooter';
import { BulkCreateNavigationHeader } from './BulkCreateNavigationHeader';
import {
	useAiBulkNavigationCurrentPage,
	useAiBulkNavigationGotoPage,
} from './AiBulkNavigationContextProvider';
import { GicPanelBody } from './GicPanelBody';
import { headerHeight } from './IssueCreateSidePanel';
import { useBulkCreateContext, useSetNextIssueToEdit } from './BulkCreateContextProvider';
import { saveCloudIdToLocalStorage } from './utils/localStorageHelpers';
import { useBulkCreateFlags } from './utils/useBulkCreateFlags';

type BulkEditIssueViewProps = {
	isAiIssue?: boolean;
};

const bulkEditContentWrapper = xcss({
	flexGrow: '1',
});

// This is the view for both AI and non-AI bulk create where users can edit and create individual issues
export const BulkEditIssueView = ({ isAiIssue = false }: BulkEditIssueViewProps) => {
	const goToPage = useAiBulkNavigationGotoPage();
	const setNextIssueToEdit = useSetNextIssueToEdit();
	const currentPage = useAiBulkNavigationCurrentPage();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	
	const [{ currentIssueId, currentJiraProject, aiBulkIssuesList }] = useBulkCreateContext();
	const currentIssue = aiBulkIssuesList.find((issue) => issue.id === currentIssueId);
	const { triggerSingleIssueCreatedFlag } = useBulkCreateFlags();

	const onClose = useCallback((isCreateSuccess) => { 
		if(!isCreateSuccess) {
			void goToPage('default')}
		}, [goToPage]);
	const isOpen = currentPage === 'edit_issue';

	const onFailure = useCallback(
		(error: Error | unknown) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'confluenceBulkIssueCreate',
					action: 'error',
					actionSubject: 'globalIssueCreate',
					attributes: {
						error,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const onSuccess = useCallback(
		(payload: IssueCreateEmbedSuccessPayload) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'confluenceBulkIssueCreate',
					action: 'created',
					actionSubject: 'issue',
					actionSubjectId: 'confluenceIssueCreate',
				},
			}).fire();

			triggerSingleIssueCreatedFlag(payload);

			// Save the cloud id in localStorage if an issue is created
			saveCloudIdToLocalStorage(payload);

			// TODO insert issues at the bottom of the page

			void setNextIssueToEdit();
		},
		[triggerSingleIssueCreatedFlag, createAnalyticsEvent, setNextIssueToEdit],
	);

	if (!currentIssue) {
		// TODO return error screen component
		return null;
	}
	const { summary, description, issueType, id: issueId } = currentIssue;

	return (
		<SidePanel isOpen={isOpen} isFixed headerHeight={headerHeight} panelTop={headerHeight}>
			<Stack testId="bulk-edit-content-wrapper" xcss={bulkEditContentWrapper}>
				<BulkCreateNavigationHeader onClose={onClose} />
				<GicPanelBody
					key={`issue-id-${issueId}`}
					onSuccess={onSuccess}
					onFailure={onFailure}
					onClose={onClose}
					analyticsSource='confluenceBulkIssueCreate'	
					summary={summary}
					descriptionAdf={description}
					issueTypeId={issueType.id}
					configuration={{ disableProjectDropdown: true, displaySitePicker: false }}
					projectId={currentJiraProject?.id} 
					/>
				{isAiIssue && <AiSubFooter />}
			</Stack>
		</SidePanel>
	);
};
